/*
 * B2B forms — step wizard, conditional fields, repeatable rows, validation.
 *
 * Shared by sections/b2b-form-reclamatii.liquid and sections/b2b-form-gdpr.liquid.
 * Both post through Shopify's native {% form 'contact' %}. Everything here is
 * progressive enhancement: with JS off every step and every conditional block
 * is visible, nothing is disabled, and the form still submits in one go.
 *
 * Markup contract
 * ---------------
 * Root:            <div data-kform="name"> … </div>
 * Steps:           <fieldset class="kform-step"> … </fieldset>
 * Wizard chrome:   .kform-bar__fill, .kform-nav__count, .kform-nav__title,
 *                  .kform-back, .kform-next, .kform-submit
 *
 * Conditional block — shown when ANY control with that key holds the value:
 *   <div class="kform-cond" data-show-when="urgent" data-show-value="Da">
 *   <input type="radio" value="Da" data-cond-key="urgent">
 *   data-show-value takes a |-separated list.
 *
 * Conditionally required — OR across ;-separated clauses:
 *   <input data-require-if="urgent=Da;tip=Posibil incident|Neconformitate">
 *
 * "At least one" checkbox group:
 *   <div class="kform-group" data-require-one data-err="…">
 *     … .kreg-check inputs …
 *     <div class="kform-err"></div>
 *   </div>
 *
 * Repeatable rows:
 *   <div data-rows="produse"> <div class="kform-row">…</div> … </div>
 *   <button type="button" data-add-row="produse">
 *
 * Hidden CONDITIONAL fields are also disabled, so a `required` control inside
 * a collapsed block can never block submission or add empty lines to the
 * notification email. Hidden STEPS are NOT disabled — their answers must still
 * post — which is why the form runs with noValidate in wizard mode and all
 * validation is driven from here.
 *
 * Why validation hangs off `click`, not `submit`
 * ----------------------------------------------
 * Shopify injects an invisible-captcha script on every {% form 'contact' %}
 * (ce_storefront_forms_captcha_hcaptcha). It listens for `submit`, cancels it,
 * fetches a token asynchronously and then calls form.submit() — the *native*
 * method, which dispatches no submit event and ignores every preventDefault we
 * made. So anything a submit handler blocks gets re-issued behind our back a
 * moment later, and a half-filled form posts. Because the token round-trip is
 * network-dependent, it only bites some of the time, which is what made this
 * look flaky rather than broken.
 *
 * The cure is to never let an invalid form produce a submit event at all:
 * cancel the click on the submit button, and route Enter through the buttons.
 * The submit listener is kept only as defence in depth.
 */
(function () {
  'use strict';

  var DEFAULT_GROUP_ERROR = 'Selectați cel puțin o opțiune.';
  var CONSENT_ERROR = 'Bifați toate declarațiile obligatorii pentru a continua.';

  function all(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  function fields(el) {
    return all(el, 'input, select, textarea');
  }

  // Idempotent by construction: the original disabled state is captured once,
  // on first touch, and every later call derives from that snapshot. Re-reading
  // `f.disabled` each pass would let a repeat disable be mistaken for the
  // field's original state, permanently trapping it.
  function setEnabled(el, on) {
    fields(el).forEach(function (f) {
      if (f.dataset.kfOrigDisabled === undefined) {
        f.dataset.kfOrigDisabled = f.disabled ? '1' : '0';
      }
      f.disabled = on ? f.dataset.kfOrigDisabled === '1' : true;
    });
  }

  function show(el, on) {
    el.hidden = !on;
    setEnabled(el, on);
  }

  // "urgent=Da;tip=A|B" -> [{k:'urgent', v:['Da']}, {k:'tip', v:['A','B']}]
  function parseClauses(spec) {
    return (spec || '')
      .split(';')
      .map(function (part) {
        var kv = part.split('=');
        return {
          k: (kv[0] || '').trim(),
          v: (kv[1] || '').split('|').map(function (x) {
            return x.trim();
          })
        };
      })
      .filter(function (c) {
        return c.k;
      });
  }

  // Dev-only escape hatch. Shopify hardcodes the /contact recipient to the
  // store contact email and nothing in the theme can override it, so the only
  // way to see a submission somewhere else is to post somewhere else. With the
  // section's "URL de test" filled in, the form is retargeted at that endpoint
  // and the Shopify routing inputs are dropped, so neither /contact nor its
  // captcha is involved. A banner makes the state impossible to miss.
  function applyTestEndpoint(root, form) {
    var url = root.dataset.kformTest;
    if (!url || !form) return;

    form.setAttribute('action', url);
    form.setAttribute('method', 'post');
    all(form, 'input[name="form_type"], input[name="utf8"]').forEach(function (i) {
      i.parentNode.removeChild(i);
    });

    var note = document.createElement('div');
    note.className = 'kreg-banner kreg-banner--err';
    note.setAttribute('role', 'alert');
    note.textContent =
      'MOD TEST: formularul se trimite către ' + url + ', nu către Klintensiv. Nu introduceți date reale.';
    form.insertBefore(note, form.firstChild);
  }

  function init(root) {
    var form = root.querySelector('form');
    applyTestEndpoint(root, form);
    var conds = all(root, '[data-show-when]');
    var reqs = all(root, '[data-require-if]');
    var controls = all(root, '[data-cond-key]');
    var groups = all(root, '[data-require-one]');

    /* ---------------- conditional logic ---------------- */

    function valuesFor(key) {
      var out = [];
      controls.forEach(function (c) {
        if (c.dataset.condKey !== key) return;
        if (c.type === 'radio' || c.type === 'checkbox') {
          if (c.checked) out.push(c.value);
        } else if (c.value) {
          out.push(c.value);
        }
      });
      return out;
    }

    function anyClauseMatches(clauses) {
      return clauses.some(function (c) {
        var have = valuesFor(c.k);
        return c.v.some(function (w) {
          return have.indexOf(w) !== -1;
        });
      });
    }

    function sync() {
      conds.forEach(function (el) {
        var have = valuesFor(el.dataset.showWhen);
        var on = (el.dataset.showValue || '').split('|').some(function (w) {
          return have.indexOf(w.trim()) !== -1;
        });
        show(el, on);
      });
      reqs.forEach(function (el) {
        var on = anyClauseMatches(parseClauses(el.dataset.requireIf));
        el.required = on;
        el.setAttribute('aria-required', on ? 'true' : 'false');
      });
    }

    controls.forEach(function (c) {
      c.addEventListener('change', sync);
    });
    sync();

    /* ---------------- repeatable rows ---------------- */

    all(root, '[data-rows]').forEach(function (wrap) {
      var rows = all(wrap, '.kform-row');
      var addBtn = root.querySelector('[data-add-row="' + wrap.dataset.rows + '"]');

      rows.forEach(function (row, i) {
        if (i > 0) show(row, false);
        var rm = row.querySelector('.kform-row__rm');
        if (!rm) return;
        rm.addEventListener('click', function () {
          fields(row).forEach(function (f) {
            if (f.type === 'checkbox' || f.type === 'radio') f.checked = false;
            else f.value = '';
          });
          show(row, false);
          if (addBtn) addBtn.hidden = false;
          sync();
        });
      });

      function nextHidden() {
        for (var i = 0; i < rows.length; i++) if (rows[i].hidden) return rows[i];
        return null;
      }

      if (addBtn) {
        addBtn.addEventListener('click', function () {
          var row = nextHidden();
          if (!row) return;
          show(row, true);
          sync();
          if (!nextHidden()) addBtn.hidden = true;
          var first = row.querySelector('input, select, textarea');
          if (first) first.focus();
        });
      }
    });

    /* ---------------- validation ---------------- */

    // A step is valid when every enabled control passes constraint validation
    // AND every "at least one" group has a selection. Returns null, or the
    // thing to complain about.
    function firstInvalidField(scope) {
      var list = fields(scope);
      for (var i = 0; i < list.length; i++) {
        if (!list[i].disabled && !list[i].checkValidity()) return list[i];
      }
      return null;
    }

    function checkGroups(scope, mark) {
      var bad = null;
      all(scope, '[data-require-one]').forEach(function (g) {
        var boxes = all(g, 'input[type="checkbox"]');
        var any = boxes.some(function (b) {
          return !b.disabled && b.checked;
        });
        var err = g.querySelector('.kform-err');
        if (!any) {
          if (!bad) bad = g;
          if (mark) {
            g.classList.add('is-invalid');
            if (err) {
              // The message is normally authored inline in the Liquid (Romanian
              // typographic quotes can't live in an HTML attribute — the
              // parser treats „ ” as delimiters). Fall back only if empty.
              if (!err.textContent.trim()) {
                err.textContent = g.dataset.err || DEFAULT_GROUP_ERROR;
              }
              err.classList.add('is-on');
            }
          }
        } else {
          g.classList.remove('is-invalid');
          if (err) err.classList.remove('is-on');
        }
      });
      return bad;
    }

    // Clear a group's error as soon as the user ticks something in it.
    groups.forEach(function (g) {
      g.addEventListener('change', function () {
        checkGroups(g, false);
      });
    });

    // The declaration checkboxes are drawn by a sibling .box; the real control
    // sits at opacity 0. reportValidity() on one of those anchors the browser
    // bubble to an invisible element — off to the side, over the next line of
    // text, and worded in the browser's language rather than ours. Flag the
    // rows ourselves instead.
    function badConsents(scope) {
      return all(scope, '.kreg-consent').filter(function (row) {
        var f = row.querySelector('input[type="checkbox"]');
        return f && !f.disabled && !f.checkValidity();
      });
    }

    function consentError(step) {
      var el = step.querySelector('.kform-err--consent');
      if (!el) {
        el = document.createElement('div');
        el.className = 'kform-err kform-err--consent';
        el.setAttribute('role', 'alert');
        el.textContent = CONSENT_ERROR;
        step.appendChild(el);
      }
      return el;
    }

    function markConsents(scope) {
      var bad = badConsents(scope);
      all(scope, '.kreg-consent').forEach(function (row) {
        row.classList.toggle('is-invalid', bad.indexOf(row) !== -1);
      });
      var err = consentError(scope);
      err.classList.toggle('is-on', bad.length > 0);
      return bad;
    }

    // Clear the flag as soon as the box is ticked.
    all(root, '.kreg-consent input[type="checkbox"]').forEach(function (b) {
      b.addEventListener('change', function () {
        var step = b.closest('.kform-step');
        if (step) markConsents(step);
      });
    });

    function reportOn(scope) {
      var f = firstInvalidField(scope);
      if (f) {
        var row = f.closest && f.closest('.kreg-consent');
        if (row) {
          markConsents(scope);
          row.scrollIntoView({ block: 'center', behavior: 'smooth' });
        } else {
          f.reportValidity();
        }
        return false;
      }
      markConsents(scope);
      var g = checkGroups(scope, true);
      if (g) {
        g.scrollIntoView({ block: 'center', behavior: 'smooth' });
        return false;
      }
      return true;
    }

    /* ---------------- wizard ---------------- */

    var steps = all(root, '.kform-step');
    var nextBtn = root.querySelector('.kform-next');
    var backBtn = root.querySelector('.kform-back');
    var submitBtn = root.querySelector('.kform-submit');
    var fill = root.querySelector('.kform-bar__fill');
    var count = root.querySelector('.kform-nav__count');
    var title = root.querySelector('.kform-nav__title');
    var chrome = all(root, '.kform-bar, .kform-nav');

    // Nothing to drive, or the form already submitted — leave it as a plain
    // long form so the success banner is what the user sees.
    if (steps.length < 2 || !nextBtn || !submitBtn || root.dataset.kformDone === '1') {
      return;
    }

    root.classList.add('is-wizard');
    chrome.forEach(function (el) {
      el.hidden = false;
    });
    if (form) form.noValidate = true;

    var at = 0;

    function stepTitle(step) {
      var lg = step.querySelector('legend');
      if (!lg) return '';
      // Drop the leading step number chip from the visible legend text.
      var n = lg.querySelector('.kform-step__n');
      return (lg.textContent || '').replace(n ? n.textContent : '', '').trim();
    }

    function goTo(i, focus) {
      at = Math.max(0, Math.min(steps.length - 1, i));
      steps.forEach(function (s, k) {
        s.hidden = k !== at;
      });
      var last = at === steps.length - 1;
      backBtn.hidden = at === 0;
      nextBtn.hidden = last;
      submitBtn.hidden = !last;
      if (fill) fill.style.width = ((at + 1) / steps.length) * 100 + '%';
      if (count) count.textContent = 'Pasul ' + (at + 1) + ' din ' + steps.length;
      if (title) title.textContent = stepTitle(steps[at]);
      if (focus) {
        root.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    }

    nextBtn.addEventListener('click', function () {
      if (!reportOn(steps[at])) return;
      goTo(at + 1, true);
    });

    backBtn.addEventListener('click', function () {
      goTo(at - 1, true);
    });

    // Validate every step — not just the last one. Fields in hidden steps stay
    // enabled so their answers post, which means the browser would otherwise
    // refuse to focus an invalid control it can't display. We reveal the
    // offending step first, then report there.
    function validateAll() {
      for (var i = 0; i < steps.length; i++) {
        if (firstInvalidField(steps[i]) || checkGroups(steps[i], false)) {
          goTo(i, true);
          reportOn(steps[i]);
          return false;
        }
      }
      return true;
    }

    // Capture phase on the button: cancelling the click stops the browser from
    // ever turning it into a submit event, so Shopify's captcha script never
    // gets the chance to re-issue it natively. See the note at the top.
    submitBtn.addEventListener(
      'click',
      function (e) {
        if (!validateAll()) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      },
      true
    );

    // Implicit submission — Enter in a single-line field — bypasses the button
    // entirely and hands a half-filled wizard straight to the captcha. Route it
    // through the buttons so it goes through validateAll() like every other
    // path. Textareas keep their newline.
    if (form) {
      form.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter' || e.defaultPrevented) return;
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        var t = e.target;
        if (!t || t.tagName === 'TEXTAREA' || t.tagName === 'BUTTON') return;
        e.preventDefault();
        (at === steps.length - 1 ? submitBtn : nextBtn).click();
      });

      // Defence in depth: a submit event we did not originate still gets
      // checked. It cannot be trusted on its own — the captcha ignores this
      // preventDefault — which is exactly why the gate above exists.
      form.addEventListener('submit', function (e) {
        if (!validateAll()) e.preventDefault();
      });
    }

    goTo(0, false);
  }

  function boot() {
    all(document, '[data-kform]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
